import { slideContentSummary } from "../speaker/scriptHash.js";
export const SURF_SPEAKER_SCRIPT_SYSTEM = [
    "You are a public speaking coach writing spoken discourse for presentation slides (Surf).",
    "Write natural spoken language — one idea per beat, smooth transitions between ideas.",
    "NEVER invent numbers, facts, quotes, or citations not present in the provided material.",
    "When references are provided, cite ONLY those references — do not add others.",
    "Respect the word budget within ±10% (count spoken words only, not markup).",
    "Return ONLY the script as Markdown (no JSON, no code fences).",
    "Markdown formatting:",
    "- Short paragraphs (one spoken beat each); optional ### beat titles sparingly.",
    "- **bold** for key concepts, terms, and takeaways.",
    "- *italic* for words to stress aloud.",
    "- <mark>highlight</mark> for the single pivotal phrase per beat (at most one per beat).",
    "Stored as Markdown compatible with the Surf speaker editor (bold, italic, mark).",
].join(" ");
export const SURF_SPEAKER_SCRIPT_TRANSFORM_SYSTEM = [
    SURF_SPEAKER_SCRIPT_SYSTEM,
    "CRITICAL: A SPEAKER SCRIPT is provided. Transform ONLY that script.",
    "Do NOT rewrite from slide bullets, slide layers, or outline notes.",
    "Do not add facts that are not already in the speaker script or in provided references.",
    "Preserve the speaker's intent, terminology, and Markdown formatting style where possible.",
].join(" ");
function wordBudget(targetSeconds, wpm) {
    return Math.max(10, Math.round((targetSeconds / 60) * wpm));
}
function slideTitle(slide) {
    return (slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim() ?? "");
}
function neighborHint(slide) {
    if (!slide)
        return "";
    const t = slideTitle(slide);
    return t ? `«${t}»` : "";
}
export function usesSpeakerScriptAsSource(mode, existingScript) {
    if (!existingScript?.trim())
        return false;
    return mode === "shorten" || mode === "expand" || mode === "rewrite";
}
export function resolveSpeakerScriptSystemPrompt(mode, existingScript) {
    return usesSpeakerScriptAsSource(mode, existingScript)
        ? SURF_SPEAKER_SCRIPT_TRANSFORM_SYSTEM
        : SURF_SPEAKER_SCRIPT_SYSTEM;
}
function transformModeLine(mode, hasReferences) {
    if (mode === "shorten") {
        return "Shorten the SPEAKER SCRIPT below to fit the word budget. Keep the same facts and voice; remove redundancy only.";
    }
    if (mode === "expand") {
        return "Expand the SPEAKER SCRIPT below toward the word budget. Elaborate existing points only — do not add new facts.";
    }
    if (mode === "rewrite" && hasReferences) {
        return "Rewrite the SPEAKER SCRIPT below integrating the PROVIDED REFERENCES (cite only these).";
    }
    return "Rewrite the SPEAKER SCRIPT below with improved flow. Same facts, no new content from slide material.";
}
function buildTransformSpeakerScriptPrompt(input) {
    const budget = wordBudget(input.targetSeconds, input.wpm);
    const script = input.existingScript.trim();
    const refBlock = input.references?.length ?
        [
            "",
            "References to integrate:",
            ...input.references.map((r, i) => `${i + 1}. ${r.title}${r.url ? ` (${r.url})` : ""}${r.snippet ? ` — ${r.snippet}` : ""}`),
        ].join("\n")
        : "";
    return [
        transformModeLine(input.mode, Boolean(input.references?.length)),
        "",
        `Language: ${input.language}`,
        `Tone: ${input.tone}`,
        `Target duration: ${input.targetSeconds}s (~${budget} words at ${input.wpm} wpm)`,
        "",
        "SPEAKER SCRIPT (transform this Markdown only — ignore slide content):",
        script,
        refBlock,
    ]
        .filter((line) => line !== "")
        .join("\n");
}
function buildGenerateFromSlidePrompt(input) {
    const budget = wordBudget(input.targetSeconds, input.wpm);
    const content = slideContentSummary(input.slide, input.deck);
    const ctx = input.deck.deckContext;
    const contextLines = [
        ctx?.summary ? `Deck summary: ${ctx.summary}` : "",
        ctx?.themes?.length ? `Themes: ${ctx.themes.join(", ")}` : "",
        ctx?.keyEntities?.length ? `Key entities: ${ctx.keyEntities.join(", ")}` : "",
    ].filter(Boolean);
    return [
        "Generate a new spoken script from the slide material.",
        "",
        `Language: ${input.language}`,
        `Tone: ${input.tone}`,
        `Target duration: ${input.targetSeconds}s (~${budget} words at ${input.wpm} wpm)`,
        `Slide ${input.slideIndex + 1} of ${input.deck.slides.length}`,
        input.slide.outlineRole ? `Slide role: ${input.slide.outlineRole}` : "",
        input.prevSlide ? `Previous slide: ${neighborHint(input.prevSlide)}` : "",
        input.nextSlide ? `Next slide: ${neighborHint(input.nextSlide)}` : "",
        "",
        ...contextLines,
        "",
        "Slide content:",
        content,
        input.outlineSection?.notes ? `Outline notes: ${input.outlineSection.notes}` : "",
    ]
        .filter(Boolean)
        .join("\n");
}
export function buildSpeakerScriptUserPrompt(input) {
    if (usesSpeakerScriptAsSource(input.mode, input.existingScript)) {
        return buildTransformSpeakerScriptPrompt(input);
    }
    return buildGenerateFromSlidePrompt(input);
}
export function parseSpeakerScriptResponse(raw) {
    let text = raw.trim();
    if (text.startsWith("```")) {
        text = text.replace(/^```[\w]*\n?/, "").replace(/\n?```$/, "").trim();
    }
    if (!text)
        throw new Error("Risposta script speaker vuota.");
    return text;
}
